/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { LoyaltyRewardOption } from "@lemmonhq/orders";
import type { LoyaltyRewardSelection } from "@lemmonhq/types";

type Props = {
  options: LoyaltyRewardOption[];
  selected: LoyaltyRewardSelection[];
  savings: number;
  payable: number;
  subtotal: number;
  currency: string;
};

/**
 * Desktop-only order summary that anchors the right column of the resolved
 * customer view. Itemises the selected rewards and totals so the wide coupon
 * list on the left reads as one half of a receipt rather than a lonely list.
 * On mobile this is hidden and the footer carries the savings/amount-due line.
 */
export const SummaryPanel = defineComponent<Props>({
  name: "SummaryPanel",
  props: ["options", "selected", "savings", "payable", "subtotal", "currency"],
  setup(props) {
    const { t } = useLocalization();
    const money = (n: number) => `${props.currency} ${n.toFixed(2)}`;
    const redeemedProducts = (option: LoyaltyRewardOption) => {
      const products = new Map<number, { name: string; quantity: number }>();
      for (const application of option.applications) {
        for (const product of application.display?.redeemed_products ?? []) {
          const current = products.get(product.id);
          products.set(product.id, {
            name: product.name,
            quantity: (current?.quantity ?? 0) + product.quantity,
          });
        }
      }
      return [...products.values()];
    };

    return () => {
      const applied = props.options.filter(
        (option) =>
          props.selected.some(
            (selection) => selection.promotion_id === option.promotion.id,
          ) && option.value > 0,
      );

      return (
        <aside class="sticky top-0 space-y-4 rounded-2xl border border-base-300 bg-base-200/40 p-4 sm:p-5">
          <h3 class="text-xs font-bold uppercase tracking-widest text-base-content/40">
            {t("loyalty.rewards.this_payment")}
          </h3>

          <dl class="space-y-2 text-sm">
            <div class="flex items-baseline justify-between gap-3 text-base-content/60">
              <dt class="truncate">{t("loyalty.rewards.customer")}</dt>
              <dd class="tabular-nums">{money(props.subtotal)}</dd>
            </div>

            {applied.length > 0 ? (
              applied.map((option) => (
                <div
                  key={option.promotion.id}
                  class="flex items-baseline justify-between gap-3"
                >
                  <dt class="min-w-0 text-base-content/70">
                    <span class="block truncate">{option.promotion.name}</span>
                    {redeemedProducts(option).map((product) => (
                      <span
                        key={product.name}
                        class="block truncate text-xs text-base-content/50"
                      >
                        {product.name} × {product.quantity}
                      </span>
                    ))}
                  </dt>
                  <dd class="shrink-0 font-semibold tabular-nums text-success">
                    −{money(option.value)}
                  </dd>
                </div>
              ))
            ) : (
              <p class="text-xs italic leading-relaxed text-base-content/40">
                {t("loyalty.rewards.select")}
              </p>
            )}
          </dl>

          <div class="space-y-2 border-t border-base-300 pt-3">
            <div class="flex items-center justify-between gap-3 text-sm">
              <span class="flex items-center gap-1.5 text-base-content/60">
                <span class="material-symbols-outlined text-base text-success">
                  savings
                </span>
                {t("loyalty.rewards.savings")}
              </span>
              <span class="font-bold tabular-nums text-success">
                −{money(props.savings)}
              </span>
            </div>
            <div class="flex items-baseline justify-between gap-3">
              <span class="text-sm font-semibold text-base-content/70">
                {t("loyalty.rewards.payable")}
              </span>
              <span class="text-2xl font-black tabular-nums text-base-content">
                {money(props.payable)}
              </span>
            </div>
          </div>
        </aside>
      );
    };
  },
});
